import { create } from "zustand";

export type TutorialPhase = "welcome" | "wallet" | "shipyard" | "farming" | "completion" | "done";

const STORAGE_KEY = "voidex-tutorial";
const SCHEMA_VERSION = 1;
const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface TutorialPersistence {
	version: number;
	completedSteps: number[];
	completedPhases: TutorialPhase[];
	isDismissed: boolean;
	currentStep: number;
	currentPhase: TutorialPhase;
	lastActiveTimestamp: number;
}

interface TutorialState {
	isActive: boolean;
	currentPhase: TutorialPhase;
	currentStep: number;
	completedSteps: Set<number>;
	completedPhases: Set<TutorialPhase>;
	isDismissed: boolean;
	isHydrated: boolean;

	startTutorial: () => void;
	nextStep: () => void;
	prevStep: () => void;
	goToStep: (step: number) => void;
	completeCurrentStep: () => void;
	dismissTutorial: () => void;
	resetTutorial: () => void;
	hydrate: () => void;
}

const PHASE_RANGES: Record<TutorialPhase, [number, number]> = {
	welcome: [0, 5],
	wallet: [6, 8],
	shipyard: [9, 11],
	farming: [12, 17],
	completion: [18, 18],
	done: [19, 19],
};

const TOTAL_STEPS = 19;

function getPhaseForStep(step: number): TutorialPhase {
	for (const [phase, [start, end]] of Object.entries(PHASE_RANGES)) {
		if (step >= start && step <= end) return phase as TutorialPhase;
	}
	return "done";
}

function persist(state: TutorialState) {
	try {
		const data: TutorialPersistence = {
			version: SCHEMA_VERSION,
			completedSteps: [...state.completedSteps],
			completedPhases: [...state.completedPhases],
			isDismissed: state.isDismissed,
			currentStep: state.currentStep,
			currentPhase: state.currentPhase,
			lastActiveTimestamp: Date.now(),
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch {
		// localStorage may be full or unavailable
	}
}

function loadPersisted(): Partial<TutorialState> | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const data: TutorialPersistence = JSON.parse(raw);
		if (data.version !== SCHEMA_VERSION) return null;

		const isStale = Date.now() - data.lastActiveTimestamp > STALE_THRESHOLD_MS;

		return {
			completedSteps: new Set(data.completedSteps),
			completedPhases: new Set(data.completedPhases),
			isDismissed: data.isDismissed,
			currentStep: isStale ? 0 : data.currentStep,
			currentPhase: isStale ? "welcome" : data.currentPhase,
		};
	} catch {
		return null;
	}
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
	isActive: false,
	currentPhase: "welcome",
	currentStep: 0,
	completedSteps: new Set(),
	completedPhases: new Set(),
	isDismissed: false,
	isHydrated: false,

	startTutorial: () => {
		set({ isActive: true, currentStep: 0, currentPhase: "welcome" });
		persist(get());
	},

	nextStep: () => {
		const state = get();
		const next = state.currentStep + 1;
		if (next > TOTAL_STEPS) {
			const completedPhases = new Set(state.completedPhases);
			completedPhases.add("completion");
			set({
				isActive: false,
				currentPhase: "done",
				completedPhases,
				completedSteps: new Set([...state.completedSteps, state.currentStep]),
			});
			persist(get());
			return;
		}
		const phase = getPhaseForStep(next);
		const completedSteps = new Set(state.completedSteps);
		completedSteps.add(state.currentStep);
		const completedPhases = new Set(state.completedPhases);
		if (getPhaseForStep(state.currentStep) !== phase) {
			completedPhases.add(getPhaseForStep(state.currentStep));
		}
		set({ currentStep: next, currentPhase: phase, completedSteps, completedPhases });
		persist(get());
	},

	prevStep: () => {
		const state = get();
		if (state.currentStep <= 0) return;
		const prev = state.currentStep - 1;
		set({ currentStep: prev, currentPhase: getPhaseForStep(prev) });
		persist(get());
	},

	goToStep: (step: number) => {
		if (step < 0 || step > TOTAL_STEPS) return;
		set({ currentStep: step, currentPhase: getPhaseForStep(step) });
		persist(get());
	},

	completeCurrentStep: () => {
		const state = get();
		const completedSteps = new Set(state.completedSteps);
		completedSteps.add(state.currentStep);
		set({ completedSteps });
		get().nextStep();
	},

	dismissTutorial: () => {
		set({ isActive: false, isDismissed: true });
		persist(get());
	},

	resetTutorial: () => {
		set({
			isActive: true,
			currentPhase: "welcome",
			currentStep: 0,
			completedSteps: new Set(),
			completedPhases: new Set(),
			isDismissed: false,
		});
		persist(get());
	},

	hydrate: () => {
		const persisted = loadPersisted();
		if (persisted) {
			set({ ...persisted, isHydrated: true });
		} else {
			// New user — auto-start tutorial
			set({ isActive: true, currentStep: 0, currentPhase: "welcome", isHydrated: true });
			persist(get());
		}
	},
}));

// Selectors
export const useTutorialActive = () => useTutorialStore((s) => s.isActive);
export const useTutorialStep = () => useTutorialStore((s) => s.currentStep);
export const useTutorialPhase = () => useTutorialStore((s) => s.currentPhase);
export const useTutorialDismissed = () => useTutorialStore((s) => s.isDismissed);
